import React, { useEffect, useState } from 'react';
import { Drawer, Form, Select, Input, Button, Space, message, Tag, Spin } from 'antd';
import pricingGroupService from '../../services/pricingGroupService';
import ifaService from '../../services/ifaService';

const { TextArea } = Input;

export default function AssignPricingGroupDrawer({ open, partner, onClose, onSuccess }) {
  const [form] = Form.useForm();
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const currentGroupId = partner?.pricing_group_id || null;
  const currentGroupName = partner?.pricing_group_name || null;

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    const loadGroups = async () => {
      setLoadingGroups(true);
      try {
        const list = await pricingGroupService.list();
        if (cancelled) return;
        setGroups(Array.isArray(list) ? list : []);
      } catch (e) {
        if (cancelled) return;
        message.error(e?.message || 'Failed to load pricing groups');
        setGroups([]);
      } finally {
        if (!cancelled) setLoadingGroups(false);
      }
    };

    loadGroups();
    form.resetFields();
    if (currentGroupId) {
      form.setFieldsValue({ pricing_group_id: currentGroupId });
    }

    return () => {
      cancelled = true;
    };
  }, [open, currentGroupId, form]);

  const handleClose = () => {
    form.resetFields();
    onClose?.();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const partnerUuid = partner?.id ?? partner?.uuid;
      if (!partnerUuid) {
        message.error('Partner identifier missing');
        return;
      }

      setSubmitting(true);
      const payload = {
        pricing_group_id: values.pricing_group_id,
        ...(values.note ? { note: String(values.note).trim() } : {}),
      };

      const res = await ifaService.assignPricingGroup(partnerUuid, payload);

      if (res?.success === false) {
        message.error(res?.message || 'Failed to assign pricing group');
        return;
      }

      if (res?.data?.skipped) {
        message.info('Pricing group unchanged');
      } else {
        message.success('Pricing group assigned');
      }

      onSuccess?.();
      handleClose();
    } catch (e) {
      if (e?.errorFields) return;
      const msg = e?.response?.data?.message || e?.message || 'Failed to assign pricing group';
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const partnerName = partner?.name || 'Partner';

  return (
    <Drawer
      title={`Assign pricing group — ${partnerName}`}
      size={420}
      open={open}
      onClose={handleClose}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="primary" loading={submitting} onClick={handleSubmit}>
            Save
          </Button>
        </Space>
      }
    >
      <div className="mb-4">
        <span className="text-xs text-gray-500 mr-2">Currently:</span>
        {currentGroupName ? (
          <Tag color="blue" className="m-0 border-0 font-medium">
            {currentGroupName}
          </Tag>
        ) : (
          <span className="text-gray-400 italic text-sm">No group assigned</span>
        )}
      </div>

      <Form form={form} layout="vertical">
        <Form.Item
          name="pricing_group_id"
          label="Pricing group"
          rules={[{ required: true, message: 'Select a pricing group' }]}
        >
          <Select
            placeholder={loadingGroups ? 'Loading groups…' : 'Select a pricing group'}
            loading={loadingGroups}
            disabled={loadingGroups}
            showSearch
            optionFilterProp="label"
            notFoundContent={loadingGroups ? <Spin size="small" /> : 'No groups found'}
            options={groups.map((g) => ({
              label: g.group_name,
              value: g.id,
            }))}
          />
        </Form.Item>

        <Form.Item name="note" label="Note (optional)">
          <TextArea rows={3} placeholder="Reason for assignment" maxLength={500} showCount />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
